import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Shield, AlertTriangle, Eye, EyeOff, Lock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginModal = ({ onLoginSuccess }) => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!acknowledged) {
      setError('You must acknowledge the security notice before proceeding.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/login`, {
        email: credentials.email,
        password: credentials.password
      });

      // Store the token and user data
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(response.data.user));

      // Call success callback
      onLoginSuccess(response.data.user);
      
    } catch (error) {
      console.error('Admin login error:', error);
      setError(
        error.response?.data?.detail || 
        'Login failed. All unauthorized access attempts are logged and monitored.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto shadow-2xl border-red-200">
        <CardHeader className="text-center bg-red-50 rounded-t-lg">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-red-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-red-800 flex items-center gap-2 justify-center">
            <Lock className="h-5 w-5" />
            Restricted Admin Access
          </CardTitle>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* Security Warning */}
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <AlertDescription className="text-red-800 font-medium">
              <div className="space-y-2">
                <p><strong>⚠️ AUTHORIZED PERSONNEL ONLY</strong></p>
                <p className="text-sm">This system is NOT open to the public. All access attempts are monitored, logged, and may be subject to investigation.</p>
                <p className="text-sm">Unauthorized access is prohibited and may result in legal action.</p>
              </div>
            </AlertDescription>
          </Alert>

          {/* Acknowledgment Checkbox */}
          <div className="flex items-start space-x-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <input
              type="checkbox"
              id="acknowledge"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1"
            />
            <label htmlFor="acknowledge" className="text-sm text-yellow-800 cursor-pointer">
              I acknowledge that I am authorized to access this system and understand that all activities are monitored and logged.
            </label>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Email Address
              </label>
              <Input
                type="email"
                placeholder="Enter admin email address"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                required
                disabled={!acknowledged}
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter admin password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  disabled={!acknowledged}
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={!acknowledged}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              disabled={loading || !acknowledged || !credentials.email || !credentials.password}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Authenticating...
                </div>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Secure Login
                </>
              )}
            </Button>
          </form>

          {/* Additional Security Notice */}
          <div className="text-xs text-gray-600 text-center space-y-1 pt-4 border-t">
            <p>🔒 All login attempts are logged with IP addresses and timestamps</p>
            <p>📊 System activity is continuously monitored for security purposes</p>
            <p>⚖️ Unauthorized access attempts will be reported to appropriate authorities</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginModal;